<template>
  <AppModal :bottom-sheet="true"
            :close-on-overlay="!loading"
            :show-close-button="!loading"
            :title="$t('general.define_competences_for_sers')"
            :visible.sync="isOpen"
            max-height="90vh"
            width="1200px"
  >
    <div class="assign-modal-content">
      <!-- Yetkinlik Seçimi -->
      <div class="assign-section">
        <div class="card assign-card">
          <div class="card-header">
            <h5 class="mb-0"> {{ $t('general.competencies') }}</h5>
          </div>
          <div class="card-body">
            <div class="form-group mb-0">
              <CompetenciesInputSelection
                  ref="competenciesInput"
                  v-model="selectedCompetencies"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Kullanıcı ve Grup Seçimi -->
      <div class="assign-section">
        <div class="card assign-card">
          <div class="card-header">
            <h5 class="mb-0"> {{ $t('general.user_and_groups') }}</h5>
          </div>
          <div class="card-body">
            <UserGroupSelection :has-competency="true"
                                :with_description="true"
                                @setCheckedGroups="($event) => {
                                  checked_groups = $event.ids;
                                  checked_groups_items = $event.items;
                                }"
                                @setCheckedUsers="($event) => {
                                  checked_users = $event.ids;
                                  checked_users_items = $event.items;
                                }"/>
          </div>
        </div>
      </div>

      <!-- Seçim Özeti -->
      <div v-if="selectedCompetencies.length > 0 || checked_users.length > 0 || checked_groups.length > 0"
           class="assign-section">
        <div class="card assign-card">
          <div class="card-header">
            <h6 class="mb-0"> {{ $t('general.choice_summary') }}</h6>
          </div>
          <div class="card-body">
            <div class="assign-summary">
              <div v-if="selectedCompetencies.length > 0" class="assign-summary-col">
                <strong> {{ $t('general.selected_competencies') }} ({{ selectedCompetencies.length }}):</strong>
                <ul class="list-unstyled mt-2">
                  <li v-for="competency in selectedCompetencies" :key="competency.id">
                    <i class="uil-check text-success mr-1"></i>
                    {{ getCompetencyName(competency) }}
                  </li>
                </ul>
              </div>

              <div v-if="checked_users.length > 0" class="assign-summary-col">
                <strong> {{ $t('general.selected_users') }} ({{ checked_users.length }}):</strong>
                <ul class="list-unstyled mt-2">
                  <li v-for="user in checked_users_items" :key="user.id">
                    <i class="uil-user text-primary mr-1"></i>
                    {{ user.name }} {{ user.surname }}
                  </li>
                </ul>
              </div>

              <div v-if="checked_groups.length > 0" class="assign-summary-col">
                <strong> {{ $t('general.selected_groups') }} ({{ checked_groups.length }}):</strong>
                <ul class="list-unstyled mt-2">
                  <li v-for="group in checked_groups_items" :key="group.id">
                    <i class="uil-users text-info mr-1"></i>
                    {{ group.name }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="assign-footer">
        <button :disabled="loading || selectedCompetencies.length === 0"
                class="btn btn-danger assign-footer-btn"
                type="button"
                @click="assignCompetenciesToUsers('remove')">
          {{ $t('general.remove_competencies_from_users') }}
        </button>
        <div class="assign-footer-right">
          <button :disabled="loading"
                  class="btn btn-outline-secondary assign-footer-btn"
                  type="button"
                  @click="closeModal">
            {{ $t('general.cancel') }}
          </button>
          <button :disabled="loading || selectedCompetencies.length === 0"
                  class="btn btn-default assign-footer-btn"
                  type="button"
                  @click="assignCompetenciesToUsers('add')">
            <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
            {{ $t('general.assign_competencies') }}
          </button>
        </div>
      </div>
    </template>
  </AppModal>
</template>

<script>
import { showSuccess, showError } from "@/utils/alertUtils";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import AppModal from "@/view/components/ui/AppModal.vue";
import UserGroupSelection from "@/view/components/assignment/UserGroupSelection";
import CompetenciesInputSelection from "@/view/components/input/CompetenciesInputSelection.vue";

import competencies_module, {
  MODULE_NAME as COMPETENCIES_MODULE_NAME,
  BULK_ASSIGN_USERS
} from "@/core/services/store/competencies/competencies.module";

export default {
  name: "UserCompetencyAssignmentModal",
  components: {
    AppModal,
    UserGroupSelection,
    CompetenciesInputSelection
  },

  data() {
    return {
      isOpen: false,
      loading: false,
      selectedCompetencies: [],
      checked_users: [],
      checked_groups: [],
      checked_users_items: [],
      checked_groups_items: [],
    };
  },

  methods: {

    getCompetencyName(competency) {
      return competency ? competency.name : this.$t('general.unknown_competency');
    },

    async assignCompetenciesToUsers(action = 'add') {
      if (this.selectedCompetencies.length === 0) {
        showError(this.$t('general.please_select_least_one_competency'));
        return;
      }

      if (this.checked_users.length === 0 && this.checked_groups.length === 0) {
        showError(this.$t('general.select_at_least_one_user_or_group'));
        return;
      }

      // yetkinlikler kullanıclardan silinirse
      if (action === 'remove') {
        const isConfirmed = await confirmDialogDelete({
          context: this,
          title: this.$t('general.remove_competencies'),
          message: this.$t('general.competency_delete_info')
        });

        if (!isConfirmed) {
          return;
        }
      }

      this.loading = true;
      try {
        const competencyIds = this.selectedCompetencies.map(comp => comp.id);

        // FormData oluştur
        const formData = new FormData();

        // Yetkinlikleri ekle
        competencyIds.forEach(id => {
          formData.append('competencies[]', id);
        });

        // Kullanıcı seçimi varsa
        if (this.checked_users.length > 0) {
          formData.append('is_user', '1');
          this.checked_users.forEach(userId => {
            formData.append('users[]', userId);
          });
        } else {
          formData.append('is_user', '0');
        }

        // Grup seçimi varsa
        if (this.checked_groups.length > 0) {
          formData.append('is_group', '1');
          this.checked_groups.forEach(groupId => {
            formData.append('groups[]', groupId);
          });
        } else {
          formData.append('is_group', '0');
        }

        // Eğer remove action ise destroy_competencies ekle
        if (action === 'remove') {
          formData.append('destroy_competencies', 'true');
        }

        await this.$store.dispatch(COMPETENCIES_MODULE_NAME + '/' + BULK_ASSIGN_USERS, {
          url: '/api/competencies/bulk-assign-users',
          contents: formData
        });

        const successMessage = action === 'remove' ? this.$t('general.competencies_successfully_removed') : this.$t('general.competencies_successfully_assigned')

        showSuccess(successMessage);
        this.closeModal();
        this.$emit('success');
      } catch (error) {
        console.error('Yetkinlik işlemi hatası:', error);
        const errorMessage = action === 'remove' ? this.$t('general.error_occurred_while_removing_competencies') : this.$t('general.error_occurred_while_assigning_competencies')

        showError(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    async openModal() {
      this.isOpen = true;

      await this.$nextTick();

      // CompetenciesInputSelection'ı yeniden yükle - guncel veri için gerekli
      if (this.$refs.competenciesInput) {
        await this.$refs.competenciesInput.refreshCompetencies();
      }
    },
    closeModal() {
      this.isOpen = false;
      this.resetForm();
    },
    resetForm() {
      this.selectedCompetencies = [];
    }
  }
};
</script>

<style scoped>
.assign-modal-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.assign-card {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  margin: 0;
}

.assign-card .card-header {
  background: #f8f9fa;
  border-radius: 10px 10px 0 0;
  padding: 12px 16px;
}

.assign-card .card-header h5,
.assign-card .card-header h6 {
  font-size: 15px;
  font-weight: 600;
}

.assign-card .card-body {
  padding: 16px;
}

.assign-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.assign-summary-col strong {
  font-size: 14px;
}

.list-unstyled li {
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.list-unstyled li:last-child {
  border-bottom: none;
}

.assign-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.assign-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.assign-footer-btn {
  border-radius: 10px;
  font-weight: 600;
  white-space: nowrap;
}

@media (max-width: 959px) {
  .assign-summary {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .assign-footer {
    flex-direction: column;
    gap: 8px;
  }

  .assign-footer-right {
    width: 100%;
    flex-direction: column;
    gap: 8px;
  }

  .assign-footer-btn {
    width: 100%;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }
}

/* Mobilde fullscreen modal */
@media (max-width: 768px) {
  ::v-deep .app-modal--bottom-sheet {
    max-height: 100vh !important;
    height: 100vh;
    border-radius: 0;
  }
}

@media (max-width: 767px) {
  .assign-card .card-body {
    padding: 12px;
  }
}
</style>
